/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * ResultsScreen.tsx - Show the results of the maths questions.
 */

import { useEffect, useRef, useState } from 'react';
import {
  StyleSheet, Text, View, TouchableOpacity, ImageBackground, Image, Animated } from 'react-native';
import { Audio } from 'expo-av';
import { TopicCategoryName } from '../../categories';
import * as congrats from './CongratsText';
import useSettingsStore from '../../hooks/useSettingsStore';
import { RootStackProps } from '../../navigation/types';
import Confetti from './Confetti';
import { useMemo } from 'react';
import { containAspectRatio, coverAspectRatio, useNavScreenDimensions } from '../../scale';
import { ImageRequire } from '../../types';
import { ColorValue } from 'react-native';
import { animateBezier } from '../../animation';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';


/**
 * Screen component to display the results, once a question set has been finished.
 *
 * Does not support its props changing under its feet. (Specifically, will not change the congrats
 * message.)
 */
const ResultsScreen = ({ route, navigation }: RootStackProps<'Results'>) => {
  const { category, results, score, type } = route.params;

  const audioEnabled = useSettingsStore(settings => settings.audioEnabled);

  // Pick random congrats message. Store it in state so we don't generate it again.
  const [message] = useState(() => {
    if (score === 0) {
      return 'Keep trying!';
    } else {
      return congrats.messages[Math.floor(Math.random() * congrats.messages.length)].message;
    }
  });

  const image = score === 0 ?
    require('../../assets/images/TinyCelebrate2_72dpi.png') :
    require('../../assets/images/TinyCelebrate1_72dpi.png');

  // Play sound
  const resultsSound = useRef<Audio.Sound>(new Audio.Sound());
  useEffect(() => {
    const currentResultsSound = resultsSound.current;

    if (audioEnabled) {
      currentResultsSound.loadAsync(require('../../assets/finished.m4a'))
        .then(() => currentResultsSound.playAsync());
    }

    return () => {
      if (audioEnabled) {
        currentResultsSound.stopAsync()
          .then(() => currentResultsSound.unloadAsync());
      }
    };
  // Disable rule: we don't want to re-run if audioEnabled changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dimens = useNavScreenDimensions();
  const insets = useSafeAreaInsets();

  // Calculate all the quantities we'll need
  const { styles, additionalNavigationDimens, circleDiameter } = useMemo(() => {
    // Background image has aspect ratio of 4096/2732 and covers the whole container.
    const backgroundDimens = coverAspectRatio({width: dimens.width, height: dimens.height}, 4096/2732);

    // The rest of the content avoids the left/right/bottom safe area insets.
    const safeAreaDimens = { width: dimens.width - insets.left - insets.right, height: dimens.height - insets.bottom };

    // Message container has aspect ratio of 0.8 and fits into at most 70% of the height of the safe area.
    const messageDimens = containAspectRatio({width: safeAreaDimens.width, height: safeAreaDimens.height * 0.7}, 0.8);

    // Additional navigation container has aspect ratio of 2 and fits into at most 25% of the height of the safe area.
    const additionalNavigationDimens = containAspectRatio({width: safeAreaDimens.width, height: safeAreaDimens.height * 0.25}, 2);
    const circleDiameter = 0.2 * additionalNavigationDimens.width;

    const styles = getResultsScreenStyles(backgroundDimens, messageDimens, additionalNavigationDimens);

    return { styles, additionalNavigationDimens, circleDiameter };
  }, [dimens, insets]);

  return (
    <ImageBackground
      source={require('../../assets/images/BackgroundLandscape.png')}
      style={styles.background}
      imageStyle={styles.backgroundImage}
    >
      <Confetti/>
      {/* Exclude: top (doesn't touch top edge) */}
      <SafeAreaView style={styles.safeArea} edges={['bottom', 'right', 'left']}>
        <View style={styles.messageContainer}>
          <Image source={image} style={styles.starsImage} />
          <Text allowFontScaling={false} style={styles.messageText}>{message}</Text>
          <View style={styles.scoreTextView}>
            <Text allowFontScaling={false} style={styles.scoreText}>You scored</Text>
            <Text allowFontScaling={false} style={styles.scoreNumberText}>
              {score}
              {' '}
              out of
              {' '}
              {results.length}
            </Text>
            <Text allowFontScaling={false} style={styles.scoreText}>in one minute on</Text>
            <Text allowFontScaling={false} style={styles.scoreText}>{category.name}</Text>
          </View>
        </View>

        <View style={styles.additionalNavigationContainer}>
          <CircleButtonWithLabel
            circleDiameter={circleDiameter}
            image={require('../../assets/images/QuestionMark_72dpi.png')}
            backgroundColor="#EF7B00"
            onClick={() => navigation.navigate('BreakDown', results)}
            width={additionalNavigationDimens.width}
            label="Show answers"
            labelBackgroundColor="#FFDE66"
          />

          <View style={styles.otherNavButtons}>
            <CircleButton
              circleDiameter={circleDiameter}
              image={require('../../assets/images/PlayAgain_72dpi.png')}
              backgroundColor="#762282"
              onClick={() => navigation.replace(type === TopicCategoryName.SUBITISING ? 'Subitising' : 'Questions', category)}/>

            <CircleButton
              circleDiameter={circleDiameter}
              image={require('../../assets/images/Home_72dpi.png')}
              backgroundColor="#69B430"
              onClick={() => navigation.navigate('Home')}/>
          </View>
        </View>
      </SafeAreaView>
    </ImageBackground>
  );
};

export default ResultsScreen;

const getResultsScreenStyles = (
  backgroundDimens: {width: number, height: number},
  messageDimens: {width: number, height: number},
  additionalNavigationDimens: {width: number, height: number}
) => StyleSheet.create({
  background: {
    flex: 1,
    overflow: 'hidden'
  },

  backgroundImage: {
    width: backgroundDimens.width,
    height: backgroundDimens.height,
    position: 'absolute',
    top: 0,
    // These are set by default but we want to align to top center, so un-set
    bottom: undefined,
    left: undefined,
    right: undefined
  },

  safeArea: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-around',
  },

  messageContainer: {
    width: messageDimens.width,
    height: messageDimens.height,
    alignItems: 'center'
  },

  starsImage: {
    height: '80%',
    width: '100%',
    resizeMode: 'contain',
  },

  messageText: {
    position: 'absolute',
    top: '20%',
    textAlign: 'center',
    fontSize: 0.072 * messageDimens.width,
    color: '#C90C6D',
    fontFamily: 'WhiteRoseMathsFont',
  },

  scoreTextView: {
    position: 'absolute',
    top: '65%',
    height: '35%',
    aspectRatio: 1.1,
    backgroundColor: '#1C3A63',
    padding: '3%',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 0.006 * messageDimens.width,
    borderColor: '#fff',
    borderRadius: 0.05 * messageDimens.width,
  },

  scoreText: {
    color: '#fff',
    fontSize: 0.05 * messageDimens.width,
    textAlign: 'center',
    fontFamily: 'WhiteRoseMathsFont',
  },

  scoreNumberText: {
    color: '#222',
    fontSize: 0.058 * messageDimens.width,
    textAlign: 'center',
    fontFamily: 'WhiteRoseMathsFont',
    backgroundColor: '#fff',
    paddingVertical: '2%',
    paddingHorizontal: '4%',
    borderRadius: 0.02 * messageDimens.width,
    marginVertical: '2%'
  },

  additionalNavigationContainer: {
    width: additionalNavigationDimens.width,
    height: additionalNavigationDimens.height,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-evenly',
  },

  otherNavButtons: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
});

interface CircleButtonProps {
  circleDiameter: number;
  image: ImageRequire;
  backgroundColor: ColorValue;
  onClick(): void;
}

const CircleButton = (props: CircleButtonProps) => {
  const {circleDiameter, image, backgroundColor, onClick} = props;
  const circleButtonStyles = useMemo(() => getCircleButtonStyles(circleDiameter, backgroundColor), [circleDiameter, backgroundColor]);

  // Animate border color on mouse over
  const mouseOverValue = useRef(new Animated.Value(0)).current;
  const borderColor = useMemo(() => mouseOverValue.interpolate({
    inputRange: [0, 1], // 0 = white, 1 = blue
    outputRange: ['rgba(255, 255, 255, 1)', 'rgba(63, 103, 156, 1)']
  }), [mouseOverValue]);

  return     (
    <TouchableOpacity
      style={circleButtonStyles.touchableRegion}
      onPress={onClick}
      onMouseEnter={() => animateBezier(mouseOverValue, 1)}
      onMouseLeave={() => animateBezier(mouseOverValue, 0)}
    >
      <Animated.View
        style={[circleButtonStyles.container, { borderColor: borderColor }]}
      >
        <Image source={image} style={circleButtonStyles.icon} />
      </Animated.View>
    </TouchableOpacity>
  );
};

const getCircleButtonStyles = (
  circleDiameter: number,
  backgroundColor: ColorValue
) => StyleSheet.create({
  touchableRegion: {
    width: circleDiameter,
    height: circleDiameter,
  },

  container: {
    width: circleDiameter,
    height: circleDiameter,
    borderWidth: circleDiameter / 20,
    borderRadius: circleDiameter / 2,
    borderStyle: 'solid',
    borderColor: '#fff',
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: backgroundColor,
  },

  icon: {
    height: '50%',
    width: '50%',
    resizeMode: 'contain',
    zIndex: 2,
  },
});

interface CircleButtonWithLabelProps extends CircleButtonProps {
  label: string;
  width: number;
  labelBackgroundColor: ColorValue;
}

const CircleButtonWithLabel = (props: CircleButtonWithLabelProps) => {
  const {circleDiameter, image, backgroundColor, onClick, label, width, labelBackgroundColor} = props;

  const circleButtonStyles = useMemo(
    () => getCircleButtonStyles(circleDiameter, backgroundColor),
    [circleDiameter, backgroundColor]
  );
  const circleButtonWithLabelStyles = useMemo(
    () => getCircleButtonWithLabelStyles(circleDiameter, width, labelBackgroundColor),
    [circleDiameter, width, labelBackgroundColor]
  );

  // Animate border color on mouse over
  const mouseOverValue = useRef(new Animated.Value(0)).current;
  const borderColor = useMemo(() => mouseOverValue.interpolate({
    inputRange: [0, 1], // 0 = white, 1 = blue
    outputRange: ['rgba(255, 255, 255, 1)', 'rgba(63, 103, 156, 1)']
  }), [mouseOverValue]);
  const letterSpacing = useMemo(() => mouseOverValue.interpolate({
    inputRange: [0, 1], // 0 = no spacing, 1 = full spacing
    outputRange: [0, width / 200]
  }), [mouseOverValue, width]);

  return (
    <TouchableOpacity
      style={circleButtonWithLabelStyles.touchableRegion}
      onPress={onClick}
      onMouseEnter={() => animateBezier(mouseOverValue, 1)}
      onMouseLeave={() => animateBezier(mouseOverValue, 0)}
    >
      <Animated.View
        style={[circleButtonStyles.container, { borderColor: borderColor }]}
      >
        <Image source={image} style={circleButtonStyles.icon} />
      </Animated.View>
      <Animated.View style={[circleButtonWithLabelStyles.labelContainer, { borderColor: borderColor }]}>
        <Animated.Text selectable={false} allowFontScaling={false} style={[circleButtonWithLabelStyles.labelText, {letterSpacing: letterSpacing}]} numberOfLines={1}>{label}</Animated.Text>
      </Animated.View>
    </TouchableOpacity>)
  ;
};

const getCircleButtonWithLabelStyles = (
  circleDiameter: number,
  width: number,
  labelBackgroundColor: ColorValue
) => StyleSheet.create({
  touchableRegion: {
    width: width,
    flexDirection: 'row',
    alignItems: 'center',
  },

  labelContainer: {
    width: width - circleDiameter/2 - 4*(circleDiameter/20),
    borderWidth: (circleDiameter/20),
    borderRadius: (circleDiameter/20),
    borderTopLeftRadius: circleDiameter/3,
    borderBottomLeftRadius: circleDiameter/3,
    borderStyle: 'solid',
    borderColor: '#fff',
    left: -circleDiameter/2,
    padding: '2%',
    paddingLeft: circleDiameter/2 + 0.05 * width,
    paddingRight: 0.05 * width,
    backgroundColor: labelBackgroundColor
  },

  labelText: {
    textAlignVertical: 'center',
    color: '#000',
    fontSize: 0.07 * width,
    fontFamily: 'WhiteRoseMathsFont',
  },
});

